/**
 * Resolve tenant context from the current hostname.
 *
 * Two modes:
 * - Subdomain (e.g., pickerel.responsekit.net): locked to that tenant, no switching.
 * - Root domain (responsekit.net / localhost): context switchable via localStorage.
 *   API calls include X-Tenant header so the backend resolves the right schema.
 */

export interface TenantContext {
  slug: string;
  name: string;
  shortName: string;
  emailDomain: string;
  wpBaseUrl: string;
  /**
   * Backend platform this tenant belongs to ("responsekit" | "patrolschedule").
   * Optional — only known dev/root-domain tenants carry this. Used solely to
   * drive the dev-only X-Platform-Override header in api/client.ts so that
   * root-domain/localhost sessions (which go through the Vite proxy, whose
   * changeOrigin:true rewrites the outgoing Host header) resolve the correct
   * platform on the backend instead of always falling back to whatever the
   * Host-based resolution picks. Never consulted on a locked subdomain — see
   * isSubdomainLocked(), where Host-based resolution alone is authoritative.
   */
  platform?: 'responsekit' | 'patrolschedule';
}

const TENANT_MAP: Record<string, TenantContext> = {
  pickerel: {
    slug: 'pickerel',
    name: 'Pickerel Fire Rescue',
    shortName: 'Pickerel EMS',
    emailDomain: 'pickerelfirerescue.org',
    wpBaseUrl: 'https://pickerelfirerescue.org',
    platform: 'responsekit',
  },
  'example_dept': {
    slug: 'example_dept',
    name: 'Example Fire & EMS Department',
    shortName: 'Example EMS',
    emailDomain: 'example.com',
    wpBaseUrl: '',
    platform: 'responsekit',
  },
  'ski_brule': {
    slug: 'ski_brule',
    name: 'Ski Brule Patrol',
    shortName: 'Ski Brule',
    emailDomain: '',
    wpBaseUrl: '',
    platform: 'patrolschedule',
  },
};

/**
 * Dev-only helper: returns the known platform for a tenant slug on the root
 * domain / localhost, or undefined if the slug isn't in TENANT_MAP (or has
 * no platform recorded). Used by api/client.ts to decide whether to send
 * X-Platform-Override — deliberately returns undefined rather than guessing
 * for unknown slugs, so unmapped tenants keep today's Host-only resolution
 * with no regression.
 */
export function getKnownTenantPlatform(slug: string): 'responsekit' | 'patrolschedule' | undefined {
  return TENANT_MAP[slug]?.platform;
}

const ACTIVE_TENANT_KEY = 'responsekit_active_tenant';

/** Returned when the hostname has a subdomain not in TENANT_MAP and is not a demo tenant.
 *  Currently unused — all unknown subdomains fall through to dynamicTenantContext().
 *  Kept for the Login.tsx "Department not found" branch, which would activate if this
 *  return path is ever restored for stricter subdomain enforcement. */
export const UNKNOWN_TENANT = Symbol('unknown_tenant');

/** True when on the bare demo.responsekit.net registration site (not a demo tenant). */
export function isDemoRegistrationSite(): boolean {
  return window.location.hostname.toLowerCase() === 'demo.responsekit.net';
}

/** True when on a demo tenant subdomain (demo-*.responsekit.net or demo_*). */
export function isDemoTenant(): boolean {
  const host = window.location.hostname.toLowerCase();
  const m = host.match(/^(demo[-_][a-z0-9]+)\.responsekit\.net$/);
  return !!m;
}

function dynamicTenantContext(slug: string): TenantContext {
  return { slug, name: slug, shortName: slug, emailDomain: '', wpBaseUrl: '' };
}

/**
 * Returns true when on a tenant subdomain (context is locked).
 * Returns false on the root domain or localhost (context is switchable).
 */
export function isSubdomainLocked(): boolean {
  const host = window.location.hostname.toLowerCase();
  if (host === 'portal.pickerelems.org') return true;
  const rkMatch = host.match(/^([a-z0-9_-]+)\.responsekit\.net$/);
  if (rkMatch && rkMatch[1] !== 'www' && rkMatch[1] !== 'dev') return true;
  const psMatch = host.match(/^([a-z0-9_-]+)\.patrolschedule\.com$/);
  if (psMatch && psMatch[1] !== 'www') return true;
  return false;
}

/**
 * Returns true when the tenant switcher should be hidden because the user is
 * locked to a specific tenant portal. Unlike isSubdomainLocked(), this returns
 * false for dev.responsekit.net since it's a shared dev environment where
 * multi-tenant switching should remain available.
 */
export function isTenantSwitcherLocked(): boolean {
  const host = window.location.hostname.toLowerCase();
  if (host === 'portal.pickerelems.org') return true;
  const rkMatch = host.match(/^([a-z0-9_-]+)\.responsekit\.net$/);
  if (rkMatch && rkMatch[1] !== 'www' && rkMatch[1] !== 'dev') return true;
  const psMatch = host.match(/^([a-z0-9_-]+)\.patrolschedule\.com$/);
  if (psMatch && psMatch[1] !== 'www') return true;
  return false;
}

/**
 * Get the active tenant slug.
 * - On a subdomain: returns the subdomain slug (locked).
 * - On root domain: returns the slug from localStorage, or the user's home tenant.
 */
export function getActiveTenantSlug(): string | null {
  const host = window.location.hostname.toLowerCase();

  // Exact alias
  if (host === 'portal.pickerelems.org') return 'pickerel';

  // Subdomain — responsekit.net (dev is a shared multi-tenant env, not a real tenant)
  const rkMatch = host.match(/^([a-z0-9_-]+)\.responsekit\.net$/);
  if (rkMatch && rkMatch[1] !== 'www' && rkMatch[1] !== 'dev') return rkMatch[1];

  // Subdomain — patrolschedule.com (and future platform domains)
  const psMatch = host.match(/^([a-z0-9_-]+)\.patrolschedule\.com$/);
  if (psMatch && psMatch[1] !== 'www') return psMatch[1];

  // Root domain or localhost — check localStorage
  const stored = localStorage.getItem(ACTIVE_TENANT_KEY);
  if (stored) return stored;

  // Localhost dev param
  if (host === 'localhost') {
    const params = new URLSearchParams(window.location.search);
    return params.get('tenant') || null;
  }

  return null;
}

/**
 * Set the active tenant slug (only meaningful on root domain).
 * Dispatches a `tenantChanged` window event so listeners can force
 * route-level remounts and re-fetch tenant-scoped data without a
 * full page reload.
 */
export function setActiveTenantSlug(slug: string): void {
  const prev = localStorage.getItem(ACTIVE_TENANT_KEY);
  localStorage.setItem(ACTIVE_TENANT_KEY, slug);
  if (prev !== slug) {
    window.dispatchEvent(new CustomEvent('tenantChanged', { detail: { slug, prev } }));
  }
}

export function resolveTenant(): TenantContext | null | typeof UNKNOWN_TENANT {
  const host = window.location.hostname.toLowerCase();

  // Exact alias matches
  if (host === 'portal.pickerelems.org') return TENANT_MAP.pickerel;

  // Subdomain of responsekit.net — slug is the leftmost label
  const rkMatch = host.match(/^([a-z0-9_-]+)\.responsekit\.net$/);
  if (rkMatch) {
    const slug = rkMatch[1];
    if (slug === 'www') return null;
    if (slug === 'demo') return null;
    if (TENANT_MAP[slug]) return TENANT_MAP[slug];
    if (slug.startsWith('demo-') || slug.startsWith('demo_')) return dynamicTenantContext(slug);
    return dynamicTenantContext(slug);
  }

  // Subdomain of patrolschedule.com (or future platform domains) — slug is the leftmost label
  const psMatch = host.match(/^([a-z0-9_-]+)\.patrolschedule\.com$/);
  if (psMatch) {
    const slug = psMatch[1];
    if (slug === 'www') return null;
    if (TENANT_MAP[slug]) return TENANT_MAP[slug];
    return dynamicTenantContext(slug);
  }

  // Root domain — resolve from localStorage
  if (host === 'responsekit.net' || host === 'www.responsekit.net'
      || host === 'patrolschedule.com' || host === 'www.patrolschedule.com') {
    const slug = localStorage.getItem(ACTIVE_TENANT_KEY);
    if (slug && TENANT_MAP[slug]) return TENANT_MAP[slug];
    if (slug) return dynamicTenantContext(slug);
    return null;
  }

  // localhost with a query param for dev — ?tenant=pickerel
  if (host === 'localhost') {
    const params = new URLSearchParams(window.location.search);
    const slug = params.get('tenant');
    if (slug && TENANT_MAP[slug]) return TENANT_MAP[slug];
    // Also check localStorage for localhost
    const stored = localStorage.getItem(ACTIVE_TENANT_KEY);
    if (stored && TENANT_MAP[stored]) return TENANT_MAP[stored];
  }

  return null;
}

/**
 * Return the canonical auth base URL — always the root domain, never a subdomain.
 *
 * The OIDC redirect_uri must be consistent regardless of which hostname the user
 * opened the app from. Using window.location.origin on a subdomain produces a
 * subdomain-specific redirect_uri, which breaks cookie scoping and trusted device
 * detection (cookies set on responsekit.net aren't sent from subdomains and vice versa).
 */
export function getAuthBaseUrl(): string {
  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost') return window.location.origin;
  // sessionStorage is partitioned per origin — the OIDC callback must land on the same
  // origin that initiated the flow, otherwise oidc_state won't be found and state
  // validation fails with "possible CSRF or stale tab". Return the current origin for
  // any subdomain (tenant portal, dev, alias, etc.) so the redirect_uri always matches.
  if (host.endsWith('patrolschedule.com')) {
    const psMatch = host.match(/^([a-z0-9_-]+)\.patrolschedule\.com$/);
    if (psMatch && psMatch[1] !== 'www') return window.location.origin;
    return 'https://patrolschedule.com';
  }
  if (host !== 'responsekit.net' && host !== 'www.responsekit.net') {
    // Any subdomain of responsekit.net (tenant portals, dev, portal alias, etc.)
    return window.location.origin;
  }
  return 'https://responsekit.net';
}
